import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref, watch } from "vue";
import TableauPopup from "../../ui-components/TableauPopup.vue";
import TableauButton from "../../ui-components/TableauButton.vue";
import TableauSelect from "../../ui-components/select/TableauSelect.vue";
import TableauRadioButton from "../../ui-components/TableauRadioButton.vue";
import TableauTextBox from "../../ui-components/TableauTextBox.vue";
import TableauSeparator from "../../ui-components/TableauSeparator.vue";
import { container } from "tsyringe";
import { CommonModule } from "../common.module";
import { GoogleSheetExportModule } from "./google-sheet-export.module";
import { ApiModule } from "../common/api/api.module";
import {
  CreateSpreadSheetRequestDTO,
  SpreadSheetListRequestDTO,
  UpdateSpreadSheetRequestDTO,
} from "../common/api/autogenerated";
const googleIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><title>google</title><path fill="currentColor" d="M21.35,11.1H12.18V13.83H18.69C18.36,17.64 15.19,19.27 12.19,19.27C8.36,19.27 5,16.25 5,12C5,7.9 8.2,4.73 12.2,4.73C15.29,4.73 17.1,6.7 17.1,6.7L19,4.72C19,4.72 16.56,2 12.1,2C6.42,2 2.03,6.8 2.03,12C2.03,17.05 6.16,22 12.25,22C17.6,22 21.5,18.33 21.5,12.91C21.5,11.76 21.35,11.1 21.35,11.1V11.1Z" /></svg>`;

export default /*#__PURE__*/_defineComponent({
  __name: 'GoogleSheetExport',
  setup(__props, { expose: __expose }) {
  __expose();

const api = container.resolve(ApiModule);
const log = container.resolve(GoogleSheetExportModule).log;
const underlyingSelector = ref<InstanceType<typeof TableauSelect> | null>(null);
const existingSheetsSelector = ref<InstanceType<typeof TableauSelect> | null>(
  null
);

const status = ref<"idle" | "loading" | "success" | "error">("idle");
const statusMessage = ref("");

const selectedViewId = ref<string[]>(["0"]);
const dataType = ref<"summary" | "underlying">("summary");
const underlyingTableId = ref<string[]>(undefined);

const googleAuthStatus = ref<"none" | "loading" | "authed">("none");
const googleEmail = ref<string>("");
const sheetMode = ref<"create" | "update">("create");
const newSheetName = ref<string>("");
const sheetFolderId = ref<string>("");
const targetSheetName = ref<string>("Tableau Data");
const selectedExistingSheetId = ref<string[]>([]);

const formValid = computed(
  () =>
    selectedViewId.value?.length === 1 &&
    (dataType.value === "summary" || underlyingTableId.value?.length === 1) &&
    googleAuthStatus.value === "authed" &&
    !!googleEmail.value &&
    ((sheetMode.value === "create" && !!newSheetName.value) ||
      (sheetMode.value === "update" &&
        selectedExistingSheetId.value.length === 1)) &&
    !!targetSheetName.value
);

async function getViews(): Promise<{ id: string; text: string }[]> {
    try {
        const vizs = window.tableau.VizManager.getVizs();
        if (!vizs || vizs.length === 0) {
            log.w("No visualizations found in Tableau VizManager.");
            return [];
        }
        const workbook = vizs[0].getWorkbook();
        if (!workbook) {
            log.w("No workbook found in the active visualization.");
            return [];
        }
        const activeSheet = workbook.getActiveSheet();
        if (!activeSheet) {
            log.w("No active sheet found in the workbook.");
            return [];
        }
        const worksheets = activeSheet.getWorksheets();
        if (!worksheets || worksheets.length === 0) {
            log.w("No worksheets found in the active sheet.");
            return [];
        }
        return worksheets.map((e: any) => ({
            id: e.getIndex().toString(),
            text: e.getName(),
        }));
    } catch (error) {
        log.e("An error occurred while retrieving views:", error);
        return [];
    }
}
watch(selectedViewId, (o, n) => {
  underlyingSelector.value?.loadItems();
});
watch(dataType, (o, n) => {
  underlyingSelector.value?.loadItems();
});
onMounted(() => {
  underlyingSelector.value?.loadItems();
});

async function getUnderlyingTables(): Promise<{ id: string; text: string }[]> {
  if (dataType.value === "summary" || !selectedViewId.value[0]) {
    return [];
  }
  const ret = (
    await window.tableau.VizManager.getVizs()[0]
      .getWorkbook()
      .getActiveSheet()
      .getWorksheets()
      [selectedViewId.value[0]].getUnderlyingTablesAsync()
  ).map((e) => ({
    id: e.getTableId(),
    text: e.getCaption(),
  }));
  underlyingTableId.value = ret.length > 0 ? [ret[0].id] : [];
  return ret;
}

async function getGoogleSession() {
  googleAuthStatus.value = "loading";
  const req = await api.apiRequest((e) => e.apiGoogleSheetOauthInfo());
  if (!req.success || !req.value?.email) {
    googleAuthStatus.value = "none";
  } else {
    googleEmail.value = req.value.email;
    googleAuthStatus.value = "authed";
  }
}
async function googleLogin() {
  let baseUrl = window.stm.config.appUrl;
  if (window.stm.config.routingPatched) {
    baseUrl = window.stm.config.serverUrl;
    if (!baseUrl.endsWith("/")) {
      baseUrl += "/";
    }
    baseUrl += "stm";
  }
  googleAuthStatus.value = "loading";
  const req = await api.apiRequest((e) => e.apiGoogleSheetOauth(baseUrl));
  if (!req.success) {
    log.e("Failed to get Google OAuth URL", req.value);
    googleAuthStatus.value = "none";
  } else {
    const authWindow = window.open(req.value, "_blank");
    const interval = setInterval(async () => {
      if (!authWindow.closed) {
        return;
      }
      clearInterval(interval);
      getGoogleSession();
    }, 500);
  }
}
async function logoutGoogle() {
  const i = googleAuthStatus.value;
  googleAuthStatus.value = "loading";
  const req = await api.apiRequest((e) => e.apiGoogleSheetOauthLogout());
  if (!req.success) {
    log.e("Failed to logout google", req.value);
    googleAuthStatus.value = i;
  } else {
    googleAuthStatus.value = "none";
  }
}

async function loadExistingSheets(): Promise<{ id: string; text: string }[]> {
  if (googleAuthStatus.value !== "authed") {
    return [];
  }
  const req = await api.apiRequest((c) => c.apiGoogleSheetList(
    new SpreadSheetListRequestDTO({
        serverId: window.stm.config.serverId,
      })
    )
  );
  if (!req.success) {
    log.e("Failed to load existing sheets", req.error);
    throw new Error("Failed to load existing sheets");
  } else {
    return req.value.map((e) => ({
      id: e.spreadSheetId,
      text: e.spreadSheetName,
    }));
  }
}
watch(googleAuthStatus, (o, n) => {
  existingSheetsSelector.value?.loadItems();
});
async function doExport() {
  if (!formValid.value) {
    return;
  }
  statusMessage.value = "Exporting data...";
  status.value = "loading";

  // get data
  let dataTable;
  switch (dataType.value) {
    case "summary":
      dataTable = await window.tableau.VizManager.getVizs()[0]
        .getWorkbook()
        .getActiveSheet()
        .getWorksheets()
        [+selectedViewId.value[0]].getSummaryDataAsync();
      break;
    case "underlying":
      dataTable = await window.tableau.VizManager.getVizs()[0]
        .getWorkbook()
        .getActiveSheet()
        .getWorksheets()
        [+selectedViewId.value[0]].getUnderlyingTableDataAsync(
          underlyingTableId.value[0]
        );
      break;
  }
  const content = [
    dataTable.getColumns().map((c: any) => c.getFieldName()),
    ...dataTable
      .getData()
      .map((d: any) => d.map((c: any) => c.value?.toString())),
  ];

  let req;
  if (sheetMode.value === 'create') {
    let folderId = sheetFolderId.value!;
    if (folderId && typeof folderId === 'string') {
      const urlPattern = /^https:\/\/drive\.google\.com\/drive\/folders\/([^/]+)\/?$/;
      const match = folderId.match(urlPattern);
      if (match) {
        folderId = match[1];
      } else if (folderId.startsWith("https://")) {
        folderId = "";
      }
    }
    req = await api.apiRequest((c) =>
      c.apiGoogleSheetCreate(
        new CreateSpreadSheetRequestDTO({
          serverId: window.stm.config.serverId,
          fileName: newSheetName.value,
          parentFolderId: folderId,
          sheetName: targetSheetName.value,
          data: content,
        })
      )
    );
  } else {
    req = await api.apiRequest((c) =>
      c.apiGoogleSheetUpdate(
        new UpdateSpreadSheetRequestDTO({
          data: content,
          serverId: window.stm.config.serverId,
          spreadSheetId: selectedExistingSheetId.value[0],
          sheetName: targetSheetName.value,
        })
      )
    );
  }
  if (!req.success) {
    log.e("Failed to upload data top Google Sheet", req.error);
    statusMessage.value = "Failed to upload to Google Sheet";
    status.value = "error";
  } else {
    status.value = "success";
    statusMessage.value = "Data upload successful";
  }
}

onMounted(() => {
  getGoogleSession();
});

const __returned__ = { googleIconSvg, api, log, underlyingSelector, existingSheetsSelector, status, statusMessage, selectedViewId, dataType, underlyingTableId, googleAuthStatus, googleEmail, sheetMode, newSheetName, sheetFolderId, targetSheetName, selectedExistingSheetId, formValid, getViews, getUnderlyingTables, getGoogleSession, googleLogin, logoutGoogle, loadExistingSheets, doExport, TableauPopup, TableauButton, TableauSelect, TableauRadioButton, TableauTextBox, TableauSeparator }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})